import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { completeFileUpload, prepareFileUpload, uploadToPresignedUrl } from '@/pages/return/rgr11V2/queries/apis'
import { FileUploadCompleteRes, FileUploadPrepareRes, ResourceType } from '@/pages/return/rgr11V2/queries/types'

/** presigned PUT TTL 이 60초라 재시도도 무한정 물고 늘어지지 않는다(3.5). */
const MAX_PUT_ATTEMPTS = 3

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status

/**
 * 1~2단계(키 발급 → S3 PUT). 409(S3 object 없음·TTL 만료)는 키가 죽은 것이므로 1단계부터
 * 다시(새 key), 503(S3 timeout·의존성 실패)은 같은 key 로 재시도한다(3.5 에러 표).
 * 그 외 에러는 즉시 올린다 — 여기서 삼키지 않는다. 재귀로 짜서 재시도 루프의 await-in-loop 를 피한다.
 */
const putWithRetry = async (
  shipper: string,
  fileName: string,
  contentType: string,
  file: Blob,
  attempt?: number,
  prepared?: FileUploadPrepareRes,
): Promise<string> => {
  const attemptNo = attempt ?? 1
  const current = prepared ?? (await prepareFileUpload({ shipper, fileName, contentType })).data
  if (!current) throw new Error('첨부 업로드 키 발급에 실패했습니다.')

  try {
    await uploadToPresignedUrl(current.uploadUrl, file, current.signedHeaders)
    return current.key
  } catch (error) {
    const status = statusOf(error)
    const isLastAttempt = attemptNo >= MAX_PUT_ATTEMPTS
    if (status === 409 && !isLastAttempt) return putWithRetry(shipper, fileName, contentType, file, attemptNo + 1)
    if (status === 503 && !isLastAttempt) return putWithRetry(shipper, fileName, contentType, file, attemptNo + 1, current)
    throw error
  }
}

export interface AttachmentUploadTask {
  file: Blob
  fileName: string
  contentType: string
  resourceType: ResourceType
}

/**
 * 파일 하나를 3단계(prepare→PUT→complete) 그대로 올린다.
 *
 * 실패해도 이미 저장된 행(3.3 결과)을 되돌리지 않는다 — 저장과 첨부는 별개 트랜잭션이다.
 * target 없음(404)·resourceType 충돌(409, complete 단계) 은 재시도하지 않고 그대로 올려
 * 호출부가 "저장은 됐고 첨부만 실패" 안내와 재시도 버튼을 UI 에 남기게 한다.
 */
export const uploadAttachment = async (
  shipper: string,
  task: AttachmentUploadTask,
  targetIds: number[],
): Promise<FileUploadCompleteRes> => {
  const key = await putWithRetry(shipper, task.fileName, task.contentType, task.file)
  const completed = await completeFileUpload({ shipper, key, resourceType: task.resourceType, targetIds })
  if (!completed.data) throw new Error('첨부 연결 응답이 비어 있습니다.')
  return completed.data
}

export interface AttachmentUploadInput {
  shipper: string
  tasks: AttachmentUploadTask[]
  targetIds: number[]
}

export interface AttachmentUploadOutcome {
  succeeded: FileUploadCompleteRes[]
  failed: { task: AttachmentUploadTask; error: unknown }[]
}

/**
 * 여러 파일(촬영 이미지 N + 녹화본)을 순서대로 올린다 — 하나가 실패해도 나머지는 계속 시도한다.
 * 실패분은 `failed` 로 모아 호출부가 재시도 UI 를 그릴 수 있게 한다.
 */
const uploadAll = async ({ shipper, tasks, targetIds }: AttachmentUploadInput): Promise<AttachmentUploadOutcome> => {
  const outcome: AttachmentUploadOutcome = { succeeded: [], failed: [] }

  await tasks.reduce(
    (chain, task) =>
      chain.then(async () => {
        try {
          outcome.succeeded.push(await uploadAttachment(shipper, task, targetIds))
        } catch (error) {
          outcome.failed.push({ task, error })
        }
      }),
    Promise.resolve(),
  )

  return outcome
}

export const useAttachmentUpload = (): UseMutationResult<AttachmentUploadOutcome, Error, AttachmentUploadInput> =>
  useMutation({
    mutationFn: uploadAll,
  })
